import { useEffect, useState } from 'react'
import { create } from 'zustand'
import type { QueueItemT } from '@/lib/types/bgSyncTypes.ts'
import type { QueueState } from '@/lib/nativeUpdateTiers'
import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'

/**
 * `unknown` until the first read SUCCEEDS.
 *
 * Why this field exists at all: the store used to start at `data: []` with nothing to
 * distinguish "read the queue, it is empty" from "never read the queue". Every consumer
 * that only counted rows was fine with that — a nav badge showing 0 before the first read
 * is harmless. A GATE is not: `data.length === 0` was about to be used to decide whether it
 * is safe to send a field user to the App Store, and answering "safe" because we had not
 * looked yet is how unsent work gets abandoned.
 *
 * The error branch is the sharper half. It used to set `error` and leave `data: []`
 * untouched, so a failed DB read left the store reporting a confirmed-empty queue. That is
 * fail-open in the most literal sense: the louder the failure, the more certain the store
 * sounded.
 */
type SyncQueueStatus = 'unknown' | 'ready' | 'error'

interface SyncQueueState {
  data: QueueItemT[]
  error: string | null
  status: SyncQueueStatus
  readAttempts: number
  queueRevision: number
  confirmedRevision: number
  noteQueueChanged: () => void
  readData: (force?: boolean) => Promise<void>
}

/**
 * A read that keeps failing must not hold a required update open forever. After this many
 * consecutive failures the queue is reported as `unreadable`, which lets the user out WITH a
 * warning instead of trapping them behind a spinner that will never resolve.
 */
export const MAX_QUEUE_READ_ATTEMPTS = 3
export const QUEUE_READ_TIMEOUT_MS = 5000

let readDataInFlight: Promise<void> | null = null

export const useSyncQueueStore = create<SyncQueueState>(set => ({
  data: [],
  error: null,
  status: 'unknown',
  readAttempts: 0,
  queueRevision: 0,
  confirmedRevision: -1,
  noteQueueChanged: () => set(state => ({ queueRevision: state.queueRevision + 1 })),
  readData: (force = false) => {
    // Single-flight. Two consumers, StrictMode, or a remount can all ask at once, and two
    // overlapping reads would let whichever settles LAST decide between `ready` and `error`.
    // A forced read is chained after the current flight so it observes writes made before the
    // forced call instead of accepting a snapshot that may have started before those writes.
    if (readDataInFlight !== null && !force) return readDataInFlight

    const performRead = async () => {
      const revisionAtStart = useSyncQueueStore.getState().queueRevision
      let timeoutTimer: ReturnType<typeof setTimeout> | undefined

      try {
        const all = await Promise.race([
          nativeQueueManager.getAllEntries(),
          new Promise<never>((_, reject) => {
            timeoutTimer = setTimeout(
              () => reject(new Error('Queue read timed out')),
              QUEUE_READ_TIMEOUT_MS,
            )
          }),
        ])
        set({
          data: all,
          error: null,
          status: 'ready',
          readAttempts: 0,
          confirmedRevision: revisionAtStart,
        })
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Queue DB not ready'
        set(state => ({
          error: message,
          status: 'error',
          readAttempts: state.readAttempts + 1,
        }))
      } finally {
        if (timeoutTimer !== undefined) clearTimeout(timeoutTimer)
      }
    }

    const readPromise =
      readDataInFlight === null ? performRead() : readDataInFlight.then(performRead, performRead)

    readDataInFlight = readPromise
    void readPromise.then(
      () => {
        if (readDataInFlight === readPromise) readDataInFlight = null
      },
      () => {
        if (readDataInFlight === readPromise) readDataInFlight = null
      },
    )
    return readPromise
  },
}))

/**
 * The queue as the update ladder needs to see it.
 *
 * `unknown` means "not read yet" and is safe to gate on because `useQueueState` itself
 * guarantees a read. Collapsing this to `hasPending: boolean` is the trap — it forces the
 * unknown case to pick a side before the queue has been inspected.
 *
 * `unreadable` is the other end: we looked, repeatedly, and could not find out. That must not
 * read as `empty` (fail-open, sends the user off with unsent work) nor stay `unknown`
 * (fail-closed forever, which is the lockout this whole change exists to remove).
 *
 * Counts EVERY row, including permanently-failed ones. A permanent failure is still work the
 * user typed and the server never received; `hasPendingQueueEntriesForType()` excludes those
 * and is the wrong instrument here for exactly that reason.
 */
export function selectQueueState(state: SyncQueueState): QueueState {
  if (state.status === 'error' && state.readAttempts >= MAX_QUEUE_READ_ATTEMPTS) {
    return 'unreadable'
  }
  if (state.status !== 'ready') return 'unknown'
  if (state.data.length === 0) return 'empty'

  // Rows that performSync() will never touch again (it filters permanentFailure out at
  // nativeQueueManager.ts:481 and :490). If NOTHING else is left, the queue cannot drain on
  // its own, and a gate that waits for it to drain waits forever.
  const syncable = state.data.filter(item => !item.metadata?.permanentFailure)
  return syncable.length > 0 ? 'pending' : 'stuck'
}

export function useQueueState(): QueueState {
  const status = useSyncQueueStore(state => state.status)
  const readAttempts = useSyncQueueStore(state => state.readAttempts)
  const queueRevision = useSyncQueueStore(state => state.queueRevision)
  const confirmedRevision = useSyncQueueStore(state => state.confirmedRevision)
  const [mountReadSettled, setMountReadSettled] = useState(false)

  // A row can be enqueued while nothing is mounted to observe it, which leaves `status: 'ready'`
  // sitting over stale `data`. Re-reading on mount is what makes "the queue was checked" true
  // instead of "the queue was checked at some earlier point".
  useEffect(() => {
    let mounted = true
    void useSyncQueueStore
      .getState()
      .readData(true)
      .finally(() => {
        if (mounted) setMountReadSettled(true)
      })

    return () => {
      mounted = false
    }
  }, [])

  // The initial read is the mount effect above; this one only retries a FAILED read, backing off
  // so a broken DB does not spin. It stops at MAX_QUEUE_READ_ATTEMPTS, after which the selector
  // reports `unreadable` and the user is let out with a warning.
  useEffect(() => {
    if (status !== 'error' || readAttempts >= MAX_QUEUE_READ_ATTEMPTS) return

    const retryTimer = setTimeout(
      () => void useSyncQueueStore.getState().readData(),
      300 * readAttempts,
    )
    return () => clearTimeout(retryTimer)
  }, [readAttempts, status])

  // Without this the read is a single snapshot: anything enqueued afterwards on a route where
  // `useNativeQueueAutoSync` is not mounted would never reach the store, and the card would go
  // on offering the store link over work it no longer knows about.
  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | undefined
    const unsubscribe = nativeQueueManager.addListener(() => {
      useSyncQueueStore.getState().noteQueueChanged()
      if (refreshTimer !== undefined) clearTimeout(refreshTimer)
      refreshTimer = setTimeout(() => void useSyncQueueStore.getState().readData(true), 100)
    })

    return () => {
      unsubscribe()
      if (refreshTimer !== undefined) clearTimeout(refreshTimer)
    }
  }, [])

  const queueState = useSyncQueueStore(selectQueueState)
  // `unreadable` is terminal and says nothing about queue contents: it is the escape hatch, so a
  // freshness gate that only a SUCCESSFUL read can satisfy must never withhold it.
  if (queueState === 'unreadable') return 'unreadable'
  return mountReadSettled && confirmedRevision === queueRevision ? queueState : 'unknown'
}
